import React from 'react'
import { useSetupWizard } from '../../../contexts/SetupWizardContext'
import { useThemeColors } from '../../../hooks/useThemeColors'
import { 
  BuildingOfficeIcon, 
  SwatchIcon, 
  HomeIcon, 
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'

export default function ReviewStep() {
  const { wizardData, validateCurrentStep, getStepErrors, isLoading } = useSetupWizard()
  const colors = useThemeColors()

  const sections = [
    {
      title: 'Organization',
      icon: BuildingOfficeIcon,
      isComplete: getStepErrors(1).length === 0,
      items: [
        { label: 'Business Name', value: wizardData.organizationProfile.businessName },
        { label: 'Business Type', value: wizardData.organizationProfile.businessType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) },
        { label: 'Phone', value: wizardData.organizationProfile.phone },
        { label: 'Email', value: wizardData.organizationProfile.email },
        { label: 'Address', value: `${wizardData.organizationProfile.address}, ${wizardData.organizationProfile.city}, ${wizardData.organizationProfile.state} ${wizardData.organizationProfile.zipCode}` }
      ]
    },
    {
      title: 'Branding',
      icon: SwatchIcon,
      isComplete: true, // Always complete (no required fields)
      items: [
        { label: 'Logo', value: wizardData.branding.logo ? 'Uploaded' : 'Not set' },
        { label: 'Primary Color', value: wizardData.branding.primaryColor, color: wizardData.branding.primaryColor },
        { label: 'Background', value: wizardData.branding.backgroundColor, color: wizardData.branding.backgroundColor },
        { label: 'Theme Mode', value: wizardData.branding.darkMode ? 'Dark' : 'Light' }
      ]
    },
    {
      title: 'Facilities',
      icon: HomeIcon,
      isComplete: getStepErrors(3).length === 0,
      items: wizardData.facilities.map(facility => ({
        label: facility.name || 'Unnamed Facility',
        value: `Capacity: ${facility.capacity} people${facility.amenities?.length ? `, ${facility.amenities.length} amenities` : ''}`
      }))
    },
    {
      title: 'Services',
      icon: ClipboardDocumentListIcon,
      isComplete: getStepErrors(4).length === 0,
      items: wizardData.services.map(service => ({
        label: service.name || 'Unnamed Service',
        value: `$${service.price.toFixed(2)} - ${service.duration} min`
      }))
    },
    {
      title: 'Packages',
      icon: CurrencyDollarIcon,
      isComplete: true, // Optional step
      items: wizardData.packages.length > 0 
        ? wizardData.packages.map(pkg => ({
            label: pkg.name || 'Unnamed Package',
            value: pkg.type === 'session_based' ? `${pkg.credits} sessions - $${pkg.price}` :
                   pkg.type === 'time_based' ? `${pkg.duration} days - $${pkg.price}` :
                   `$${pkg.price}`
          }))
        : [{ label: 'No packages added', value: 'You can add them later' }]
    },
    {
      title: 'Memberships',
      icon: CreditCardIcon,
      isComplete: true, // Optional step
      items: wizardData.memberships.length > 0 
        ? wizardData.memberships.map(membership => ({
            label: membership.name || 'Unnamed Membership',
            value: `$${membership.price}/${membership.period || 'month'}`
          }))
        : [{ label: 'No memberships added', value: 'You can add them later' }]
    },
    {
      title: 'Schedule Templates',
      icon: CalendarDaysIcon,
      isComplete: true, // Optional step
      items: wizardData.scheduleTemplates.length > 0 
        ? wizardData.scheduleTemplates.map(schedule => {
            const totalSlots = schedule.instances.length
            return {
              label: schedule.name || 'Unnamed Schedule',
              value: `${schedule.recurrence} - ${totalSlots} time${totalSlots !== 1 ? 's' : ''} per week`
            }
          })
        : [{ label: 'No schedules added', value: 'You can set them up later' }]
    },
    {
      title: 'Team',
      icon: UsersIcon,
      isComplete: true, // Optional step
      items: wizardData.teamMembers.length > 0
        ? wizardData.teamMembers.map(member => ({
            label: member.name || 'Unnamed Member',
            value: `${member.email} (${member.role})`
          }))
        : [{ label: 'No team members added', value: 'You can invite them later' }]
    }
  ]

  const allRequiredComplete = sections.every(section => section.isComplete)

  return (
    <div className="review-step">
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: colors.text
      }}>
        Review your setup
      </h2>
      <p style={{
        fontSize: '1rem',
        color: colors.textSecondary,
        marginBottom: '2rem'
      }}>
        Let's make sure everything looks good before we launch your facility.
      </p>

      {/* Completion Status */}
      <div style={{
        padding: '1rem',
        backgroundColor: allRequiredComplete ? `${colors.success}20` : `${colors.warning}20`,
        border: `1px solid ${allRequiredComplete ? colors.success : colors.warning}40`,
        borderRadius: '8px',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        {allRequiredComplete ? (
          <>
            <CheckCircleIcon style={{ 
              width: '24px', 
              height: '24px', 
              color: colors.success,
              flexShrink: 0
            }} />
            <div>
              <p style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: colors.success,
                margin: 0
              }}>
                All set! Your facility is ready to launch.
              </p>
              <p style={{
                fontSize: '0.813rem',
                color: colors.text,
                margin: 0,
                marginTop: '0.25rem'
              }}>
                Click "Launch My Facility" to complete setup and start accepting bookings.
              </p>
            </div>
          </>
        ) : (
          <>
            <ExclamationCircleIcon style={{ 
              width: '24px', 
              height: '24px', 
              color: colors.warning,
              flexShrink: 0
            }} />
            <div>
              <p style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: colors.warning,
                margin: 0
              }}>
                Some required information is missing
              </p>
              <p style={{
                fontSize: '0.813rem',
                color: colors.text,
                margin: 0,
                marginTop: '0.25rem'
              }}>
                Please go back and complete the highlighted sections.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Sections Review */}
      <div style={{
        display: 'grid',
        gap: '1.5rem'
      }}>
        {sections.map((section, index) => {
          const Icon = section.icon
          return (
            <div
              key={index}
              style={{
                backgroundColor: colors.background,
                border: `1px solid ${section.isComplete ? colors.border : colors.warning}`,
                borderRadius: '8px',
                padding: '1.5rem',
                position: 'relative'
              }}
            >
              {/* Section Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  backgroundColor: section.isComplete ? `${colors.primary}20` : `${colors.warning}20`
                }}>
                  <Icon style={{ 
                    width: '18px', 
                    height: '18px', 
                    color: section.isComplete ? colors.primary : colors.warning
                  }} />
                </div>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: colors.text,
                  margin: 0,
                  flex: 1
                }}>
                  {section.title}
                </h3>
                {section.isComplete ? (
                  <CheckCircleIcon style={{ 
                    width: '20px', 
                    height: '20px', 
                    color: colors.success 
                  }} />
                ) : (
                  <ExclamationCircleIcon style={{ 
                    width: '20px', 
                    height: '20px', 
                    color: colors.warning 
                  }} />
                )}
              </div>

              {/* Section Items */}
              <div style={{
                display: 'grid',
                gap: '0.5rem'
              }}>
                {section.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.875rem',
                      paddingLeft: '2.5rem'
                    }}
                  >
                    <span style={{ color: colors.textSecondary }}>
                      {item.label}:
                    </span>
                    <span style={{ 
                      color: colors.text,
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      {item.color && (
                        <span style={{
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          backgroundColor: item.color,
                          borderRadius: '4px',
                          border: '1px solid rgba(0,0,0,0.1)'
                        }} />
                      )}
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Help Text */}
      <p style={{
        textAlign: 'center',
        fontSize: '13px',
        color: colors.textSecondary,
        marginTop: '24px',
        marginBottom: '12px'
      }}>
        ✅ Your coaching space is ready (physical facilities optional)
      </p>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: colors.background,
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <div className="spinner" style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 1rem'
            }} />
            <p style={{
              fontSize: '1rem',
              color: colors.text,
              margin: 0
            }}>
              Setting up your facility...
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: colors.textSecondary,
              margin: '0.5rem 0 0'
            }}>
              This may take a few moments
            </p>
          </div>
        </div>
      )}

      <style>{`
        .spinner {
          border: 4px solid rgba(156, 163, 175, 0.3);
          border-top-color: #9CA3AF;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  )
}